import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import {
  useGetSkillsV2ApplyQuery,
  usePostSkillV2ApplyMutation,
  useDeleteSkillV2ApplyMutation,
} from '@core/app/api/applyJobV2Api'
import type { ApplyV2TempSkill } from '@core/app/types/applyV2Type'
import './ApplyV2SkillsStep.scss'
import { useTranslation } from 'react-i18next'

/* ================= DEBUG LOGGER ================= */
const DBG = {
  on: false, // увімкнути логи => true
  log: (...a: any[]) => console.log('[SkillsStep]', ...a),
  grp(title: string, fn: () => void) {
    if (!this.on) return
    console.groupCollapsed(`[SkillsStep] ${title}`); try { fn() } finally { console.groupEnd() }
  }
}

interface SkillItem {
  id: number
  name: string
  uid?: string
}

type OptionItem =
  | { type: 'add'; label: string; value: string }
  | { type: 'label'; label: string }
  | { type: 'suggestion'; label: string; id: number }

const baseClass = 'ApplyV2SkillsStep'

const SkillsStep: React.FC = () => {
  const { t } = useTranslation()
  const { getTitle } = useApplyV2NavTranslations()
  const { postProfileData, onNextStep, isPostProfileLoading, isPostApplied, settingsData } = useApplyV2Context()
  const uid = postProfileData?.data?.uid ?? ''

  const [inputValue, setInputValue] = useState('')
  const [selectedSkills, setSelectedSkills] = useState<SkillItem[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const [isKbNav, setIsKbNav] = useState(false) // ← режим клавіатури

  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const { data } = useGetSkillsV2ApplyQuery({ query: inputValue }, { skip: inputValue.length < 2 })
  const [addSkill, { isLoading: isAddLoading }] = usePostSkillV2ApplyMutation()
  const [deleteSkill] = useDeleteSkillV2ApplyMutation()

  const isSkillsRequired = settingsData?.steps?.skill?.required && settingsData?.steps?.skill?.visible

  const suggestions: { id: number; name: string }[] =
    data?.data?.map((s: any) => ({ id: s.id, name: s.name })) ?? []

  // ---------- Utils ----------
  const norm = (s: string) =>
    s.normalize('NFD').replace(/\p{Diacritic}/gu, '').trim().toLowerCase()

  const normalizedInput = norm(inputValue)
  const matchesSuggestion = normalizedInput.length > 0 && suggestions.some((s) => norm(s.name) === normalizedInput)
  const matchesSelected = normalizedInput.length > 0 && selectedSkills.some((s) => norm(s.name) === normalizedInput)

  const filteredSuggestions = useMemo(
    () => suggestions.filter((s) => !selectedSkills.some((sel) => norm(sel.name) === norm(s.name))),
    [suggestions, selectedSkills]
  )

  const hasDropdownContent =
    (!!inputValue && !matchesSuggestion && !matchesSelected) || filteredSuggestions.length > 0

  const shouldShowDropdown = isOpen && !!inputValue && hasDropdownContent

  // Побудова лінійного списку опцій
  const showAdd = !matchesSuggestion && !matchesSelected && !!inputValue

  const optionItems: OptionItem[] = useMemo(() => {
    const items: OptionItem[] = []
    if (showAdd) {
      items.push({
        type: 'add' as const,
        label: `${t('179', 'Add')} '${inputValue}'`,
        value: inputValue
      })
    }
    if (filteredSuggestions.length > 0) {
      items.push({ type: 'label' as const, label: t('7310', 'Suggestions') as string })
      items.push(...filteredSuggestions.map((s): OptionItem => (
        { type: 'suggestion' as const, label: s.name, id: s.id }
      )))
    }
    return items
  }, [showAdd, filteredSuggestions, inputValue, t])

  const selectableIndexes = useMemo(
    () => optionItems.map((it, idx) => (it.type !== 'label' ? idx : -1)).filter((i) => i !== -1),
    [optionItems]
  )

  // ---------- Handlers ----------
  const closeDropdown = () => {
    setIsOpen(false)
    setActiveIndex(-1)
    setIsKbNav(false)
  }

  const handleAddSkill = async (name: string, skillTagId?: number) => {
    if (!uid) return
    const exists = selectedSkills.find((s) => norm(s.name) === norm(name))
    if (exists) return

    try {
      setInputValue('')
      const response = await addSkill({
        uid,
        body: { skill: name, skillTagId: skillTagId ?? 0, rating: 0 },
      }).unwrap()
      const skillUid = response?.data?.skill?.uid ?? undefined

      setSelectedSkills((prev) => [...prev, { id: skillTagId ?? Date.now(), name, uid: skillUid }])
      closeDropdown()
    } catch (err) {
      console.error(err)
    }
  }

  const handleRemoveSkill = async (skill: SkillItem) => {
    if (!uid || !skill.uid) return
    try {
      setSelectedSkills((prev) => prev.filter((s) => s.name !== skill.name))
      await deleteSkill({ uid: postProfileData?.data?.uid, skillUid: skill.uid })
    } catch (err) {
      console.error(err)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = e as any

    if (key === 'Escape') {
      closeDropdown()
      return
    }

    // ↓ відкриває і фокусується на першому
    if (key === 'ArrowDown' || key === 'Down') {
      setIsKbNav(true)
      if (!shouldShowDropdown) {
        if (inputValue) {
          e.preventDefault()
          setIsOpen(true)
          setActiveIndex(selectableIndexes.length ? selectableIndexes[0] : -1)
        }
        return
      }
      e.preventDefault()
      if (!selectableIndexes.length) return
      const current = activeIndex === -1 ? selectableIndexes[0] : activeIndex
      const pos = Math.max(0, selectableIndexes.indexOf(current))
      const nextPos = Math.min(pos + 1, selectableIndexes.length - 1)
      setActiveIndex(selectableIndexes[nextPos])
      return
    }

    // ↑ рух вгору
    if (key === 'ArrowUp' || key === 'Up') {
      setIsKbNav(true)
      if (!shouldShowDropdown) return
      e.preventDefault()
      if (!selectableIndexes.length) return
      const current = activeIndex === -1 ? selectableIndexes[0] : activeIndex
      const pos = Math.max(0, selectableIndexes.indexOf(current))
      const nextPos = Math.max(pos - 1, 0)
      setActiveIndex(selectableIndexes[nextPos])
      return
    }

    // Enter / NumpadEnter — підтвердити вибір
    if (key === 'Enter' || key === 'NumpadEnter') {
      e.preventDefault()
      if (!shouldShowDropdown) {
        if (inputValue.trim() !== '') handleAddSkill(inputValue.trim())
        return
      }
      if (activeIndex === -1) {
        if (inputValue.trim() !== '') handleAddSkill(inputValue.trim())
        return
      }
      const item = optionItems[activeIndex]
      if (item?.type === 'add') {
        handleAddSkill(item.value.trim())
      } else if (item?.type === 'suggestion') {
        handleAddSkill(item.label, item.id)
      }
      return
    }
  }

  const handleFocus = () => {
    if (inputValue) {
      setIsOpen(true)
      setActiveIndex(selectableIndexes.length ? selectableIndexes[0] : -1)
    }
  }

  const handleChange = (val: string) => {
    setInputValue(val)
    setIsOpen(!!val)
  }

  const handleDropdownMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    // щоб не закривався дропдаун при кліку всередині
    e.preventDefault()
  }

  // Закриття дропдауна по кліку поза ним
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!wrapperRef.current) return
      if (!wrapperRef.current.contains(e.target as Node)) {
        closeDropdown()
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [])

  // Початкові скіли з профілю
  useEffect(() => {
    if (!postProfileData?.data?.skill) return
    const initialSkills: SkillItem[] = postProfileData.data.skill
      .filter((s) => s.skill)
      .map((s: ApplyV2TempSkill) => ({ id: s.skillTagId ?? Date.now(), name: s.skill!, uid: s.uid }))
    setSelectedSkills(initialSkills)
  }, [postProfileData?.data?.skill])

  // Ресет активного елемента при зміні контенту дропдауна
  useEffect(() => {
    if (!shouldShowDropdown) {
      setActiveIndex(-1)
      return
    }
    setActiveIndex(selectableIndexes.length ? selectableIndexes[0] : -1)
  }, [shouldShowDropdown, selectableIndexes.length])


  const stripDiacritics = (s: string) =>
    s.normalize('NFD').replace(/\p{Diacritic}/gu, '');

  const indexOfNormalized = (text: string, query: string) => {
    const t = stripDiacritics(text).toLowerCase();
    const q = stripDiacritics(query).toLowerCase().trim();
    if (!q) return -1;
    return t.indexOf(q);
  };

  const renderHighlighted = (label: string, query: string) => {
    const idx = indexOfNormalized(label, query);
    if (idx < 0) return label; 
    
    const before = label.slice(0, idx);
    const match = label.slice(idx, idx + query.length);
    const after = label.slice(idx + query.length);

    return (
      <>
        {before}
        <span className="SkillStep__match">{match}</span>
        {after}
      </>
    );
  };

  // Діагностика (опційно)
  useEffect(() => {
    DBG.grp('render snapshot', () => {
      DBG.log('inputValue:', inputValue)
      DBG.log('isOpen:', isOpen, 'shouldShowDropdown:', shouldShowDropdown)
      DBG.log('isKbNav:', isKbNav)
      DBG.log('optionItems:', optionItems)
      DBG.log('selectableIndexes:', selectableIndexes)
      DBG.log('activeIndex:', activeIndex)
    })
  }, [inputValue, isOpen, shouldShowDropdown, isKbNav, optionItems, selectableIndexes, activeIndex])

  return (
    <div className="SkillStep" ref={wrapperRef}>
      <div className="SkillStep__input-wrapper">
        <div className="SkillStep__skill-title">{t('6757', 'Add skills')}</div>
        <input
          ref={inputRef}
          className="SkillStep__input"
          placeholder={t('6798', 'Type a skill') as string} 
          value={inputValue}
          onFocus={handleFocus}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-autocomplete="list"
          aria-expanded={shouldShowDropdown}
          aria-controls="skillstep-dropdown"
          role="combobox"
        />

        {shouldShowDropdown && (
          <div
            id="skillstep-dropdown"
            className={`SkillStep__dropdown ${isKbNav ? 'is-kb-nav' : ''}`}
            onMouseDown={handleDropdownMouseDown}
            onMouseMove={() => setIsKbNav(false)}
            role="listbox"
            aria-label="Skills suggestions"
          >
            {optionItems.map((item, idx) => {
              if (item.type === 'label') {
                return (
                  <div
                    key={`label-${idx}`}
                    className="SkillStep__option SkillStep__option--label"
                    aria-hidden
                  >
                    {item.label}
                  </div>
                )
              }

              const isActive = activeIndex === idx
              const commonProps = {
                className: `SkillStep__option ${isActive ? 'is-active' : ''}`,
                onMouseEnter: () => setActiveIndex(idx),
                role: 'option' as const,
                'aria-selected': isActive,
              }

              if (item.type === 'add') {
                return (
                  <div
                    key={`add-${idx}`}
                    {...commonProps}
                    className={`SkillStep__option SkillStep__option--add ${isActive ? 'is-active' : ''}`}
                    onClick={() => handleAddSkill(item.value.trim())}
                  >
                    {item.label}
                  </div>
                )
              }

              // suggestion
              return (
                <div
                  key={item.id}
                  {...commonProps}
                  onClick={() => handleAddSkill(item.label, item.id)}
                >
                  {renderHighlighted(item.label, inputValue)}
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="SkillStep__tags">
        {selectedSkills.map((skill) => (
          <div key={skill.name} className="SkillStep__tag">
            {skill.name}
            <button
              type="button"
              className="SkillStep__tag-remove"
              aria-label={`Remove ${skill.name}`}
              onClick={() => handleRemoveSkill(skill)}
            >
              ✕
            </button>
          </div>
        ))}
        {isAddLoading && (
          <ApplySkeleton isLoading>
            <div className="SkillStep__tag">…</div>
          </ApplySkeleton>
        )}
      </div>



      <div className={`ApplyDialogV2__next ${baseClass}__next`}>
        {!settingsData?.steps?.skill?.required && (
          <ApplyV2Button
            variant={'transparent'}
            className={`ApplyDialogV2__next--btn ApplyDialogV2__next--btn-skip`}
            rightIcon={'>'}
            onClick={() => {
              onNextStep()
            }}
          >
            {getTitle('6107', 'Skip')}
          </ApplyV2Button>
        )}
        <ApplyV2Button
          type="submit"
          variant={'primary'}
          className={`ApplyDialogV2__next--btn ApplyDialogV2__next--btn-submit`}
          onClick={onNextStep}
          isLoading={isPostApplied || isPostProfileLoading}
          isDisabled={
            isPostApplied || isPostProfileLoading || (isSkillsRequired && selectedSkills.length === 0)
          }
        >
          {getTitle('3035', 'Next')}
        </ApplyV2Button>
      </div>
    </div>
  )
}

export default SkillsStep
